"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const NAV_ITEMS: Array<{ label: string; href: string }> = [
  { label: "Use cases", href: "/use-cases" },
  { label: "Roadmap", href: "/roadmap" },
  { label: "Events", href: "/events" },
  { label: "Useful links", href: "/useful-links" },
  { label: "About", href: "/about" },
];

function isActive(pathname: string, href: string) {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

export default function SiteHeader() {
  const pathname = usePathname() ?? "/";
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 -mx-4 md:-mx-8 border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <nav className="px-4 md:px-8">
        <div className="container mx-auto">
          <div className="flex h-20 items-center justify-between">
            <Link
              href="/"
              prefetch={false}
              className="hover:text-muted-foreground flex items-center gap-3 text-2xl md:text-3xl font-bold"
            >
              <svg className="size-7 md:size-8" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M12 0V9.8161H24L12 24V14.1839H0L12 0Z" />
              </svg>
              RDS4X.org
            </Link>

            <ul className="hidden md:flex items-center gap-6 lg:gap-10 text-lg lg:text-xl">
              {NAV_ITEMS.map((item) => {
                const active = isActive(pathname, item.href);
                return (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      prefetch={false}
                      className={cn(
                        "hover:text-muted-foreground transition-colors",
                        active && "font-medium underline underline-offset-8 decoration-2"
                      )}
                      aria-current={active ? "page" : undefined}
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger
                className="md:hidden inline-flex items-center justify-center size-10 -mr-2 rounded-md hover:bg-secondary/50"
                aria-label="Open menu"
              >
                <Menu className="size-6" />
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetTitle className="text-lg font-medium mb-6">Menu</SheetTitle>
                <ul className="flex flex-col gap-4 text-lg">
                  {NAV_ITEMS.map((item) => {
                    const active = isActive(pathname, item.href);
                    return (
                      <li key={item.href}>
                        <SheetClose asChild>
                          <Link
                            href={item.href}
                            prefetch={false}
                            className={cn(
                              "block py-2",
                              active && "font-medium underline underline-offset-8 decoration-2"
                            )}
                            aria-current={active ? "page" : undefined}
                          >
                            {item.label}
                          </Link>
                        </SheetClose>
                      </li>
                    );
                  })}
                </ul>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
}
